import { InputControl } from '@/components/input-control';
import { JSONControl } from '@/components/json-control';
import { RadioGroupControl } from '@/components/radiogroup-control';
import { SettingsPanel } from '@/components/settings-panel';
import { SwitchControl } from '@/components/switch-control';
import { SettingsContainer } from '@/node-editor/components';
import { EditorWindowContext } from '@/node-editor/components/preview/editor-window';
import { postMessage } from '@/hooks/useMessage';
import {
  getNodeOverride,
  hasNodeOverride,
} from '@/node-editor/store/selectors';
import { ManagerConferenceContext } from '@/store';
import { INode, INodeProps } from '@/types/node';
import { Grid } from '@mui/material';
import React, { FC, useContext, useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { IContent } from './contract';
import {
  buildConferencePayload,
  CustomRegistrationFieldsEditor,
  DEFAULT_REGISTRATION_JSON,
} from './customFields';

interface INodeExtended extends Omit<INode, 'content'> {
  content: IContent;
}

interface IProps extends Omit<INodeProps, 'node'> {
  node: INodeExtended;
}

export const Settings = ({
  node: {
    id,
    type,
    override,
    content: {
      textTagSelect,
      countryCode,
      hiddenFields = {
        title: false,
        company: false,
        city: false,
        country: false,
        phone: false,
        linkedInProfile: false,
        ecard: false,
        description: false,
        customFields: false,
      },
    },
  },
}: IProps) => {
  const { conferenceData, actions } = useContext(ManagerConferenceContext);
  const conference = conferenceData.conference || {};

  const editorWindow = useContext(EditorWindowContext);
  const sendConferencePatch = (json: string) => {
    const iframe = document.getElementById(
      'content-preview'
    ) as HTMLIFrameElement | null;
    if (!iframe?.contentWindow) return;
    let parsed;
    try {
      parsed = JSON.parse(json);
    } catch {
      return; // don't push invalid JSON to the preview
    }
    postMessage(
      {
        type: 'preview_window',
        payload: {
          slug: editorWindow?.slug,
          type: 'CONFERENCE_PATCH',
          payload: { registration_fields: parsed },
        },
      },
      iframe.contentWindow
    );
  };

  // The value the conference already has (pretty-printed), used both to seed the
  // editor and to detect whether anything actually changed on close.
  const savedJson = useMemo(
    () =>
      conference.registration_fields
        ? JSON.stringify(conference.registration_fields, null, 3)
        : '',
    [conference.registration_fields]
  );

  const watchedCustomFields = useWatch({ name: 'hiddenFields.customFields' });
  const showCustomFields =
    watchedCustomFields ?? hiddenFields.customFields ?? false;

  // Latest editor value, read once on unmount. Refs so the unmount effect
  // always sees current values without re-subscribing.
  const initialJson = savedJson || DEFAULT_REGISTRATION_JSON;
  const jsonRef = useRef(initialJson);
  const dirtyRef = useRef(false);

  // `savedJson` may arrive after mount (conference hydrates late), so the ref's
  // initial seed can be the default. Adopt the saved value once it's available,
  // but never clobber edits the user has already made.
  useEffect(() => {
    if (!dirtyRef.current && savedJson) {
      jsonRef.current = savedJson;
    }
  }, [savedJson]);

  // Persist to the conference only when the settings dialog closes (Apply or
  // Cancel both unmount this component). No API call on toggle or while typing.
  // The toggle only controls editor visibility — it never clears the saved
  // JSON, so we always persist the latest edited value.
  // Skip the call entirely when nothing changed, so Cancel is non-destructive.
  useEffect(() => {
    return () => {
      const desired = jsonRef.current;
      const current = savedJson || '{}';
      if (desired === current) return;
      try {
        JSON.parse(desired);
      } catch {
        return;
      }
      // Push the new fields into the preview iframe (instant, no reload) as the
      // dialog closes on Apply, then persist to the server.
      sendConferencePatch(desired);
      actions.conferenceUpdate(
        conference.id,
        buildConferencePayload(conference, desired)
      );
    };
  }, []);

  return (
    <>
      <SettingsPanel label="Content" initialOpen>
        <RadioGroupControl
          row
          label="Dates to list"
          name="countryCode"
          defaultValue={countryCode}
          options={[
            {
              label: 'English',
              value: 'EN',
            },
            {
              label: 'Swedish',
              value: 'SE',
            },
          ]}
        />
        <InputControl
          name="textTagSelect"
          defaultValue={textTagSelect}
          fullWidth
          variant="outlined"
          label="Override Tag description"
        />
        What input fiedls should show?
        <Grid container spacing={1}>
          <Grid size={{ xs: 6 }}>
            <SwitchControl
              name="hiddenFields.title"
              label="Title"
              defaultValue={hiddenFields.title}
              options={[false, true]}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <SwitchControl
              name="hiddenFields.company"
              label="Organisation"
              defaultValue={hiddenFields.company}
              options={[false, true]}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <SwitchControl
              name="hiddenFields.city"
              label="City"
              defaultValue={hiddenFields.city}
              options={[false, true]}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <SwitchControl
              name="hiddenFields.country"
              label="Country"
              defaultValue={hiddenFields.country}
              options={[false, true]}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <SwitchControl
              name="hiddenFields.phone"
              label="Phone"
              defaultValue={hiddenFields.phone}
              options={[false, true]}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <SwitchControl
              name="hiddenFields.linkedInProfile"
              label="LinkedIn profile"
              defaultValue={hiddenFields.linkedInProfile}
              options={[false, true]}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <SwitchControl
              name="hiddenFields.ecard"
              label="E Card (image / avatar)"
              defaultValue={hiddenFields.ecard}
              options={[false, true]}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <SwitchControl
              name="hiddenFields.description"
              label="Description"
              defaultValue={hiddenFields.description}
              options={[false, true]}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <SwitchControl
              name="hiddenFields.customFields"
              label="Custom registration field"
              defaultValue={hiddenFields.customFields}
              options={[true, false]}
            />
          </Grid>
        </Grid>
        {showCustomFields && (
          <CustomRegistrationFieldsEditor
            // Seed from the ref, not `initialJson`, so re-opening the editor
            // after toggling off restores the last edited value instead of
            // falling back to the saved/default JSON.
            initialJson={jsonRef.current}
            onJsonChange={(value) => {
              jsonRef.current = value;
              dirtyRef.current = true;
            }}
          />
        )}
      </SettingsPanel>
      <SettingsPanel
        label="Css override"
        initialOpen={useMemo(() => hasNodeOverride(type, override), [])}
      >
        <JSONControl
          id={id}
          name="override"
          defaultValue={getNodeOverride(type, override)}
        />
      </SettingsPanel>
    </>
  );
};

export const Edit: FC<IProps> = ({ children, ...props }) => {
  return (
    <SettingsContainer>
      <Settings {...props} />
    </SettingsContainer>
  );
};
